import React, { useRef, useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';

const AVAILABILITY_STATUS = [
  { value: 'FULL_TIME', label: 'Full-time' },
  { value: 'PART_TIME', label: 'Part-time' },
  { value: 'EVENINGS_WEEKENDS', label: 'Evenings/Weekends' }
];

const CONTACT_PREFERENCES = [
  { value: 'EMAIL', label: 'Email' },
  { value: 'PHONE', label: 'Phone' },
  { value: 'SECURE_MESSAGING', label: 'Secure Messaging' }
];

const SIGNATURE_FONTS = [
  { label: 'Great Vibes', value: 'Great Vibes' },
  { label: 'Dancing Script', value: 'Dancing Script' },
  { label: 'Allura', value: 'Allura' },
  { label: 'Sacramento', value: 'Sacramento' },
  { label: 'Parisienne', value: 'Parisienne' },
  { label: 'Yellowtail', value: 'Yellowtail' },
  { label: 'Alex Brush', value: 'Alex Brush' },
  { label: 'Marck Script', value: 'Marck Script' },
  { label: 'Satisfy', value: 'Satisfy' }
];

const SIGNATURE_COLORS = ['#1f2937', '#0f172a', '#1e3a8a', '#2563eb', '#0f766e', '#111827', '#000000'];

const normalizeDropboxUrl = (shareUrl) => {
  if (!shareUrl) return '';
  return shareUrl
    .replace('www.dropbox.com', 'dl.dropboxusercontent.com')
    .replace('dropbox.com', 'dl.dropboxusercontent.com')
    .replace('?dl=0', '?raw=1');
};

const OnboardingForm = ({ isOpen, onClose, onSubmit, initialData = {} }) => {
  const [formData, setFormData] = useState({
    hasCourtAppealReg: initialData.hasCourtAppealReg || false,
    registrationNo: initialData.registrationNo || '',
    city: initialData.city || '',
    address: initialData.address || '',
    country: initialData.country || '',
    availabilityStatus: initialData.availabilityStatus || AVAILABILITY_STATUS[0].value,
    contactPreference: initialData.contactPreference || CONTACT_PREFERENCES[0].value,
    contactDetailsValue: initialData.contactDetailsValue || '',
    acctNo: initialData.acctNo || '',
    bankName: initialData.bankName || '',
    acctName: initialData.acctName || '',
    stampImgUrl: initialData.stampImgUrl || '',
    signatureImgUrl: initialData.signatureImgUrl || ''
  });

  const [files, setFiles] = useState({
    stampImg: null,
    signatureImg: null
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [signatureTab, setSignatureTab] = useState('type');
  const [signatureText, setSignatureText] = useState(initialData.name || '');
  const [signatureFont, setSignatureFont] = useState(SIGNATURE_FONTS[0].value);
  const [signatureColor, setSignatureColor] = useState(SIGNATURE_COLORS[0]);
  const [signaturePreview, setSignaturePreview] = useState('');
  const [signatureFilePreview, setSignatureFilePreview] = useState('');
  const [signaturePreviewError, setSignaturePreviewError] = useState(false);
  const signatureCanvasRef = useRef(null);
  const signatureCanvasSize = useRef({ width: 640, height: 220 });
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef(null);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.country.trim()) {
      newErrors.country = 'Country is required';
    }

    if (!formData.contactDetailsValue.trim()) {
      newErrors.contactDetailsValue = 'Contact details are required';
    } else if (formData.contactPreference === 'EMAIL' && !/\S+@\S+\.\S+/.test(formData.contactDetailsValue)) {
      newErrors.contactDetailsValue = 'Please enter a valid email address';
    }

    if (formData.hasCourtAppealReg && !formData.registrationNo.trim()) {
      newErrors.registrationNo = 'Registration number is required for court appeal registration';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Create FormData object for file uploads
      const submitData = new FormData();

      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (key !== 'stampImgUrl' && key !== 'signatureImgUrl') {
          submitData.append(key, formData[key]);
        }
      });

      // Add files with correct field names
      if (files.stampImg) {
        submitData.append('stampImg', files.stampImg);
      }
      if (files.signatureImg) {
        submitData.append('signatureImg', files.signatureImg);
      }

      await onSubmit(submitData);
      toast.success('Profile updated successfully!');
      onClose();
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleFileUpload = (field, file) => {
    if (!file) return;

    // Store the actual file for form submission
    const fileField = field === 'stampImgUrl' ? 'stampImg' : 'signatureImg';
    setFiles(prev => ({
      ...prev,
      [fileField]: file
    }));

    // Update the URL field for display purposes
    handleChange(field, file.name);
    toast.info(`${field === 'stampImgUrl' ? 'Stamp' : 'Signature'} selected successfully!`);
  };

  const dataUrlToFile = (dataUrl, filename) => {
    const [header, base64] = dataUrl.split(',');
    const mimeMatch = header.match(/data:(.*?);base64/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new File([bytes], filename, { type: mime });
  };

  const renderTypedSignature = useCallback(() => {
    const text = signatureText.trim();
    if (!text) {
      setSignaturePreview('');
      return;
    }

    const { width, height } = signatureCanvasSize.current;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = signatureColor;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `52px "${signatureFont}", cursive`;
    ctx.fillText(text, width / 2, height / 2);

    setSignaturePreview(canvas.toDataURL('image/png'));
  }, [signatureText, signatureFont, signatureColor]);

  useEffect(() => {
    if (signatureTab === 'type') {
      renderTypedSignature();
    }
  }, [signatureTab, renderTypedSignature]);

  useEffect(() => {
    setSignaturePreviewError(false);

    if (!files.signatureImg) {
      setSignatureFilePreview('');
      return;
    }

    const objectUrl = URL.createObjectURL(files.signatureImg);
    setSignatureFilePreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [files.signatureImg]);

  useEffect(() => {
    if (signatureTab !== 'draw' || !signatureCanvasRef.current) return;

    const canvas = signatureCanvasRef.current;
    const { height } = signatureCanvasSize.current;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const cssWidth = rect.width || signatureCanvasSize.current.width;

    canvas.width = cssWidth * dpr;
    canvas.height = height * dpr;
    canvas.style.width = '100%';
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = signatureColor;
    ctx.clearRect(0, 0, cssWidth, height);
  }, [signatureTab, signatureColor]);

  const getCanvasPoint = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const handleDrawStart = (e) => {
    if (signatureTab !== 'draw') return;
    const point = getCanvasPoint(e);
    if (!point) return;
    isDrawingRef.current = true;
    lastPointRef.current = point;
  };

  const handleDrawMove = (e) => {
    if (!isDrawingRef.current || signatureTab !== 'draw') return;
    const canvas = signatureCanvasRef.current;
    const point = getCanvasPoint(e);
    if (!canvas || !point || !lastPointRef.current) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = signatureColor;
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastPointRef.current = point;
  };

  const handleDrawEnd = () => {
    isDrawingRef.current = false;
    lastPointRef.current = null;
  };

  const clearDrawnSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
  };

  const applyGeneratedSignature = async () => {
    if (signatureTab === 'upload') {
      toast.info('Signature upload is already selected.');
      return;
    }

    let dataUrl = '';
    if (signatureTab === 'type') {
      dataUrl = signaturePreview;
    } else if (signatureTab === 'draw') {
      const canvas = signatureCanvasRef.current;
      if (canvas) {
        dataUrl = canvas.toDataURL('image/png');
      }
    }

    if (!dataUrl) {
      toast.error('Please create a signature before saving.');
      return;
    }

    const file = dataUrlToFile(dataUrl, 'signature.png');
    setFiles(prev => ({ ...prev, signatureImg: file }));
    handleChange('signatureImgUrl', 'signature.png');
    toast.success('Signature saved!');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="flex justify-between items-center border-b border-gray-200 pb-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Complete Your Profile
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Please provide the required information to complete your translator profile.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Court Appeal Registration */}
            <div className="md:col-span-2">
              <div className="flex items-center">
                <input
                  id="hasCourtAppealReg"
                  type="checkbox"
                  checked={formData.hasCourtAppealReg}
                  onChange={(e) => handleChange('hasCourtAppealReg', e.target.checked)}
                  className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="hasCourtAppealReg" className="text-sm font-medium text-gray-700">
                  I am registered on a Court of Appeal list (for French translators on official lists)
                </label>
              </div>
            </div>

            {/* Registration Number */}
            {formData.hasCourtAppealReg && (
              <div className="md:col-span-2">
                <label htmlFor="registrationNo" className="block text-sm font-medium text-gray-700 mb-2">
                  Registration Number/ID *
                </label>
                <input
                  type="text"
                  id="registrationNo"
                  value={formData.registrationNo}
                  onChange={(e) => handleChange('registrationNo', e.target.value)}
                  className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                    errors.registrationNo ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter your registration number"
                />
                {errors.registrationNo && (
                  <p className="mt-1 text-sm text-red-600">{errors.registrationNo}</p>
                )}
              </div>
            )}

            {/* City */}
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                City *
              </label>
              <input
                type="text"
                id="city"
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  errors.city ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter your city"
              />
              {errors.city && (
                <p className="mt-1 text-sm text-red-600">{errors.city}</p>
              )}
            </div>

            {/* Country */}
            <div>
              <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                Country *
              </label>
              <input
                type="text"
                id="country"
                value={formData.country}
                onChange={(e) => handleChange('country', e.target.value)}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  errors.country ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter your country"
              />
              {errors.country && (
                <p className="mt-1 text-sm text-red-600">{errors.country}</p>
              )}
            </div>

            {/* Address */}
            <div className="md:col-span-2">
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                Address *
              </label>
              <textarea
                id="address"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                rows={3}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  errors.address ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter your full address"
              />
              {errors.address && (
                <p className="mt-1 text-sm text-red-600">{errors.address}</p>
              )}
            </div>

            {/* Availability Status */}
            <div>
              <label htmlFor="availabilityStatus" className="block text-sm font-medium text-gray-700 mb-2">
                Availability Status *
              </label>
              <select
                id="availabilityStatus"
                value={formData.availabilityStatus}
                onChange={(e) => handleChange('availabilityStatus', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {AVAILABILITY_STATUS.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Contact Preference */}
            <div>
              <label htmlFor="contactPreference" className="block text-sm font-medium text-gray-700 mb-2">
                Contact Preference *
              </label>
              <select
                id="contactPreference"
                value={formData.contactPreference}
                onChange={(e) => handleChange('contactPreference', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {CONTACT_PREFERENCES.map((preference) => (
                  <option key={preference.value} value={preference.value}>
                    {preference.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Contact Details Value */}
            <div className="md:col-span-2">
              <label htmlFor="contactDetailsValue" className="block text-sm font-medium text-gray-700 mb-2">
                Contact Details *
              </label>
              <input
                type={formData.contactPreference === 'EMAIL' ? 'email' : 'text'}
                id="contactDetailsValue"
                value={formData.contactDetailsValue}
                onChange={(e) => handleChange('contactDetailsValue', e.target.value)}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  errors.contactDetailsValue ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder={
                  formData.contactPreference === 'EMAIL' ? 'Enter your email address' :
                  formData.contactPreference === 'PHONE' ? 'Enter your phone number' :
                  'Enter your preferred contact method'
                }
              />
              {errors.contactDetailsValue && (
                <p className="mt-1 text-sm text-red-600">{errors.contactDetailsValue}</p>
              )}
            </div>

            {/* Bank Details */}
            <div className="md:col-span-2">
              <h4 className="text-md font-medium text-gray-900 mb-4">Bank Details (Optional)</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="acctNo" className="block text-sm font-medium text-gray-700 mb-2">
                    Account Number
                  </label>
                  <input
                    type="text"
                    id="acctNo"
                    value={formData.acctNo}
                    onChange={(e) => handleChange('acctNo', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Account number"
                  />
                </div>
                <div>
                  <label htmlFor="bankName" className="block text-sm font-medium text-gray-700 mb-2">
                    Bank Name
                  </label>
                  <input
                    type="text"
                    id="bankName"
                    value={formData.bankName}
                    onChange={(e) => handleChange('bankName', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Bank name"
                  />
                </div>
                <div>
                  <label htmlFor="acctName" className="block text-sm font-medium text-gray-700 mb-2">
                    Account Name
                  </label>
                  <input
                    type="text"
                    id="acctName"
                    value={formData.acctName}
                    onChange={(e) => handleChange('acctName', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Account holder name"
                  />
                </div>
              </div>
            </div>

            {/* Stamp Upload */}
            <div>
              <label htmlFor="stampUpload" className="block text-sm font-medium text-gray-700 mb-2">
                Stamp Image (Optional)
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div className="flex text-sm text-gray-600">
                    <label htmlFor="stampUpload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                      <span>Upload stamp</span>
                      <input
                        id="stampUpload"
                        name="stampUpload"
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={(e) => handleFileUpload('stampImgUrl', e.target.files[0])}
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                  {(formData.stampImgUrl || files.stampImg) && (
                    <p className="text-xs text-green-600">
                      ✓ {files.stampImg ? `Selected: ${files.stampImg.name}` : 'Stamp uploaded'}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Signature Creator */}
            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Signature (Optional)
                </label>
                {(formData.signatureImgUrl || files.signatureImg || signaturePreview) && (
                  <span className="text-xs text-green-600">Signature ready</span>
                )}
              </div>

              {(initialData.signatureImgUrl || signatureFilePreview) && (
                <div className="mb-4 flex items-center gap-3">
                  <span className="text-xs text-gray-500">Current signature:</span>
                  <div className="border border-gray-200 rounded-md px-3 py-2 bg-white">
                    {!signaturePreviewError ? (
                      <img
                        src={signatureFilePreview || normalizeDropboxUrl(initialData.signatureImgUrl)}
                        alt="Current signature"
                        className="h-12 object-contain"
                        onError={() => setSignaturePreviewError(true)}
                      />
                    ) : (
                      <span className="text-xs text-gray-400">Unable to load signature preview.</span>
                    )}
                  </div>
                </div>
              )}

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="flex flex-wrap border-b border-gray-200 bg-gray-50">
                  {[
                    { id: 'type', label: 'Type' },
                    { id: 'draw', label: 'Draw' },
                    { id: 'upload', label: 'Upload Image' }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setSignatureTab(tab.id)}
                      className={`px-4 py-2 text-sm font-medium border-r border-gray-200 ${
                        signatureTab === tab.id
                          ? 'bg-white text-green-700'
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                  <div className="ml-auto flex items-center gap-2 px-4 py-2">
                    <span className="text-xs text-gray-500">Color</span>
                    <div className="flex items-center gap-1">
                      {SIGNATURE_COLORS.map(color => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setSignatureColor(color)}
                          className={`w-5 h-5 rounded-full border ${
                            signatureColor === color ? 'border-gray-900' : 'border-gray-300'
                          }`}
                          style={{ backgroundColor: color }}
                          aria-label={`Select ${color}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-white">
                  {signatureTab === 'type' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Type your name</label>
                          <input
                            type="text"
                            value={signatureText}
                            onChange={(e) => setSignatureText(e.target.value)}
                            placeholder="Type your signature"
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Font</label>
                          <p className="text-xs text-gray-500">Pick a style below</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {SIGNATURE_FONTS.map(font => {
                          const previewName = signatureText.trim() || 'Alex Appleseed';
                          const isSelected = signatureFont === font.value;
                          return (
                            <button
                              key={font.value}
                              type="button"
                              onClick={() => setSignatureFont(font.value)}
                              className={`w-full border rounded-md px-4 py-3 text-left transition ${
                                isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div
                                className="text-2xl text-gray-800 mt-1"
                                style={{ fontFamily: `"${font.value}", cursive`, color: signatureColor }}
                              >
                                {previewName}
                              </div>
                            </button>
                          );
                        })}
                      </div>

                      <div className="border border-dashed border-gray-300 rounded-md p-4 bg-gray-50">
                        {signaturePreview ? (
                          <img src={signaturePreview} alt="Signature preview" className="max-w-full h-28 object-contain mx-auto" />
                        ) : (
                          <p className="text-sm text-gray-500 text-center">Type your name to preview the signature.</p>
                        )}
                      </div>
                    </div>
                  )}

                  {signatureTab === 'draw' && (
                    <div className="space-y-3">
                      <p className="text-xs text-gray-500">Draw your signature using mouse or touch.</p>
                      <div className="border border-gray-300 rounded-md bg-white">
                        <canvas
                          ref={signatureCanvasRef}
                          onPointerDown={handleDrawStart}
                          onPointerMove={handleDrawMove}
                          onPointerUp={handleDrawEnd}
                          onPointerLeave={handleDrawEnd}
                          className="w-full touch-none"
                        />
                      </div>
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={clearDrawnSignature}
                          className="text-sm text-gray-600 hover:text-gray-800"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  )}

                  {signatureTab === 'upload' && (
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                      <div className="space-y-1 text-center">
                        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div className="flex text-sm text-gray-600">
                          <label htmlFor="signatureUpload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                            <span>Upload signature</span>
                            <input
                              id="signatureUpload"
                              name="signatureUpload"
                              type="file"
                              accept="image/*"
                              className="sr-only"
                              onChange={(e) => handleFileUpload('signatureImgUrl', e.target.files[0])}
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                        {(formData.signatureImgUrl || files.signatureImg) && (
                          <p className="text-xs text-green-600">
                            ✓ {files.signatureImg ? `Selected: ${files.signatureImg.name}` : 'Signature uploaded'}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                  <p className="text-xs text-gray-500">
                    Signatures created here are provided as-is and may not be legally binding.
                  </p>
                  <button
                    type="button"
                    onClick={applyGeneratedSignature}
                    disabled={signatureTab === 'upload'}
                    className={`px-3 py-1.5 text-sm font-medium text-white rounded-md ${
                      signatureTab === 'upload' ? 'bg-gray-300 cursor-not-allowed' : 'bg-green-300 hover:bg-green-400'
                    }`}
                  >
                    Save signature
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={loading}
              >
                Skip for now
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {loading && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {loading ? 'Saving...' : 'Complete Profile'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OnboardingForm;
